import './style.scss';
import includes from 'lodash/includes';
import difference from 'lodash/difference';
import { TransportMode } from '../../../models/transport-mode.enum';
import { TransportSubMode } from '../../../models/transport-sub-mode.enum';
import TransportModeCheckbox from './components/TransportModeCheckbox';
import TransportSubModeCheckbox from './components/TransportSubModeCheckbox';
import { pseudoTransportSubModeOtherTrains } from '../../../helpers/transport-sub-mode.helper';

function calculateTransportSubModeStringsToShow(transportSubModes: TransportSubMode[], groupNonVasttagenAsOtherTrains: boolean): string[] {
  return groupNonVasttagenAsOtherTrains && transportSubModes.includes(TransportSubMode.regionalTrain) && transportSubModes.includes(TransportSubMode.longDistanceTrain)
    ? (transportSubModes.filter((t) => t !== TransportSubMode.regionalTrain && t !== TransportSubMode.longDistanceTrain) as string[]).concat([pseudoTransportSubModeOtherTrains])
    : transportSubModes;
}

function calculateSelectedTransportModes(
  currentSelectedTransportModes: TransportMode[],
  currentSelectedTransportSubModes: TransportSubMode[],
  changedTransportMode: TransportMode | undefined,
  changedTransportSubModeString: string | undefined,
  newTransportModeIsChecked: boolean | undefined,
  newTransportSubModeIsChecked: boolean | undefined,
): TransportMode[] {
  let result: TransportMode[];
  if (changedTransportMode !== undefined) {
    result = newTransportModeIsChecked ? [...currentSelectedTransportModes, changedTransportMode] : currentSelectedTransportModes.filter((x) => x !== changedTransportMode);
  } else {
    result = currentSelectedTransportModes;
  }

  // Check if TransportMode.train is not checked but one of the train sub modes is, if so add TransportMode.train
  if (changedTransportSubModeString && !includes(result, TransportMode.train)) {
    if (
      newTransportSubModeIsChecked &&
      includes([TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain, TransportSubMode.vasttagen, pseudoTransportSubModeOtherTrains], changedTransportSubModeString)
    ) {
      result = [...result, TransportMode.train];
    }
  }

  // Check if TransportMode.train is checked but none of the train sub modes are, if so remove TransportMode.train
  if (changedTransportSubModeString && includes(result, TransportMode.train)) {
    if (
      !newTransportSubModeIsChecked &&
      difference(
        currentSelectedTransportSubModes,
        changedTransportSubModeString === pseudoTransportSubModeOtherTrains
          ? [TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain]
          : [changedTransportSubModeString as TransportSubMode],
      ).length === 0
    ) {
      result = result.filter((x) => x !== TransportMode.train);
    }
  }

  return result;
}

function calculateSelectedTransportSubModes(currentSelectedTransportSubModes: TransportSubMode[], changedTransportSubModeString: string, newTransportSubModeIsChecked: boolean): TransportSubMode[] {
  if (changedTransportSubModeString === pseudoTransportSubModeOtherTrains) {
    return newTransportSubModeIsChecked
      ? [...currentSelectedTransportSubModes, TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain]
      : [...currentSelectedTransportSubModes.filter((x) => x !== TransportSubMode.longDistanceTrain && x !== TransportSubMode.regionalTrain)];
  }
  return newTransportSubModeIsChecked
    ? [...currentSelectedTransportSubModes, changedTransportSubModeString as TransportSubMode]
    : [...currentSelectedTransportSubModes.filter((x) => x !== changedTransportSubModeString)];
}

function showTransportSubModeAsChecked(transportSubMode: string, selectedTransportSubModes: TransportSubMode[], groupNonVasttagenAsOtherTrains: boolean): boolean {
  return groupNonVasttagenAsOtherTrains && transportSubMode === pseudoTransportSubModeOtherTrains
    ? selectedTransportSubModes.includes(TransportSubMode.regionalTrain) && selectedTransportSubModes.includes(TransportSubMode.longDistanceTrain)
    : (selectedTransportSubModes?.includes(transportSubMode as TransportSubMode) ?? false);
}

function TransportModeFilter({
  availableTransportModes,
  availableTransportSubModes = [],
  selectedTransportModes = [],
  selectedTransportSubModes = [],
  groupNonVasttagenAsOtherTrains = false,
  alwaysRenderInVerticalLayout = false,
  className,
  isInvalid = false,
  renderAsChips = false,
  onChange,
}: {
  availableTransportModes: TransportMode[];
  availableTransportSubModes?: TransportSubMode[];
  selectedTransportModes?: TransportMode[];
  selectedTransportSubModes?: TransportSubMode[];
  groupNonVasttagenAsOtherTrains?: boolean;
  alwaysRenderInVerticalLayout?: boolean;
  className?: string;
  isInvalid?: boolean;
  renderAsChips?: boolean;
  onChange: (selectedTransportModes: TransportMode[], selectedTransportSubModes: TransportSubMode[]) => void;
}) {
  function handleTransportModeCheckboxChange(transportMode: TransportMode, checked: boolean) {
    const updatedTransportModes = calculateSelectedTransportModes(selectedTransportModes, selectedTransportSubModes, transportMode, undefined, checked, undefined);
    onChange(updatedTransportModes, selectedTransportSubModes);
  }

  function handleTransportSubModeCheckboxChange(transportSubMode: string, checked: boolean) {
    const updatedTransportModes = calculateSelectedTransportModes(selectedTransportModes, selectedTransportSubModes, undefined, transportSubMode, undefined, checked);
    const updatedTransportSubModes = calculateSelectedTransportSubModes(selectedTransportSubModes, transportSubMode, checked);
    onChange(updatedTransportModes, updatedTransportSubModes);
  }

  return (
    <div
      className={`transport-mode-filter d-flex ${!renderAsChips ? 'flex-column gap-2' : 'transport-mode-filter--as-chips px-2 px-lg-0 gap-1 mx-lg-0'} ${alwaysRenderInVerticalLayout ? '' : 'flex-sm-row flex-sm-wrap'} ${className ?? ''}`}
    >
      {availableTransportModes.map((transportMode) => (
        <TransportModeCheckbox
          key={transportMode}
          className={`${renderAsChips ? '' : 'transport-mode-filter__checkbox'} mb-0`}
          isInvalid={isInvalid}
          checked={selectedTransportModes.indexOf(transportMode) !== -1}
          transportMode={transportMode}
          renderAsChips={renderAsChips}
          onChange={handleTransportModeCheckboxChange}
        />
      ))}
      {calculateTransportSubModeStringsToShow(availableTransportSubModes, groupNonVasttagenAsOtherTrains).map((transportSubMode) => (
        <TransportSubModeCheckbox
          key={transportSubMode}
          className="transport-mode-filter__checkbox mb-0"
          isInvalid={isInvalid}
          checked={showTransportSubModeAsChecked(transportSubMode, selectedTransportSubModes, groupNonVasttagenAsOtherTrains)}
          transportMode={TransportMode.train}
          transportSubMode={transportSubMode}
          onChange={handleTransportSubModeCheckboxChange}
        />
      ))}
    </div>
  );
}

export default TransportModeFilter;
